import { useGetPost } from '@repo/api/post/useGetPost';
import { format } from 'date-fns';
import { UserIcon } from 'lucide-react';
import { useMemo } from 'react';

import ErrorState from '@/components/features/meeting/ErrorState';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { getAvatar } from '@/utils/getAvatar/getAvatar';

interface PostReadReceiptsDialogProps {
  postId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function PostReadReceiptsDialog(postReadReceiptsDialogProps: PostReadReceiptsDialogProps) {
  const { postId, open, onOpenChange } = postReadReceiptsDialogProps;
  const { data, isPending, isError, errorMessage } = useGetPost(postId, open);

  // Most recent readers first
  const readReceipts = useMemo(() => [...(data?.readReceipts ?? [])].sort((a, b) => b.postreadreceipts.createdAt.localeCompare(a.postreadreceipts.createdAt)), [data]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'md:max-w-96'}>
        <DialogBody>
          <DialogHeader className={'border-border-primary flex-col border-b'}>
            <DialogTitle>Odczytane{data ? ` (${readReceipts.length})` : ''}</DialogTitle>
            <DialogDescription className="sr-only">Post został odczytany przez poniższych użytkowników</DialogDescription>
          </DialogHeader>
          <div className={'flex flex-col gap-6 overflow-auto p-4'}>
            {isError ? (
              <ErrorState errorMessage={errorMessage} />
            ) : isPending ? (
              <PostReadReceiptsSkeleton />
            ) : readReceipts.length > 0 ? (
              <ul className={'flex-1 overflow-y-auto'}>
                {readReceipts.map((receipt) => (
                  <li key={receipt.id} className={'flex items-center gap-3 overflow-hidden rounded-sm py-2'}>
                    <Avatar size={'md'}>
                      <AvatarImage src={getAvatar(receipt.avatarURL)} alt={'avatar'} />
                      <AvatarFallback>
                        <UserIcon />
                      </AvatarFallback>
                    </Avatar>
                    <div className={'flex w-full flex-col gap-0.5 overflow-hidden'}>
                      <p className={'text-text-secondary w-full truncate text-sm'}>
                        {receipt.name} {receipt.surname}
                      </p>
                      <small className={'text-text-tertiary text-xs'}>{format(receipt.postreadreceipts.createdAt, 'dd MMM RRRR, HH:mm:ss')}</small>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={'text-text-tertiary py-12 text-center text-sm'}>Nikt jeszcze nie odczytał posta</p>
            )}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function PostReadReceiptsSkeleton() {
  return (
    <div className={'flex flex-col gap-3'} aria-busy>
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className={'flex items-center gap-3'}>
          <Skeleton className={'size-10 rounded-full'} />
          <Skeleton className={'h-4 w-40'} />
        </div>
      ))}
    </div>
  );
}
